/**
 * Country Utilities for Multi-Country Support
 * 
 * This module provides utility functions for handling country-specific
 * functionality in the BrauksimKopa multi-country expansion.
 */

import { FEATURES, getCountryName, getAvailableCountries, isCountryAvailable } from '../config/features';

/**
 * Google Maps configuration for each supported country
 * Optimizes map view and bounds for better user experience
 */
export const COUNTRY_MAP_CONFIGS = {
  'LV': {
    center: { lat: 56.9677, lng: 24.1056 },
    zoom: 7,
    bounds: {
      north: 58.0855,
      south: 55.6747,
      east: 28.2414,
      west: 20.9716
    },
    region: 'lv'
  },
  'EE': {
    center: { lat: 58.5953, lng: 25.0136 },
    zoom: 7,
    bounds: {
      north: 59.7,
      south: 57.5,
      east: 28.2,
      west: 21.5
    },
    region: 'ee'
  },
  'LT': {
    center: { lat: 55.1694, lng: 23.8813 },
    zoom: 7,
    bounds: {
      north: 56.5,
      south: 53.9,
      east: 26.8,
      west: 20.9
    },
    region: 'lt'
  },
  'SE': {
    center: { lat: 60.1282, lng: 18.6435 },
    zoom: 5,
    bounds: {
      north: 69.1,
      south: 55.3,
      east: 24.2,
      west: 11.0
    },
    region: 'se'
  },
  'NO': {
    center: { lat: 60.4720, lng: 8.4689 },
    zoom: 5,
    bounds: {
      north: 71.2,
      south: 57.9,
      east: 31.3,
      west: 4.6
    },
    region: 'no'
  },
  'DK': {
    center: { lat: 56.2639, lng: 9.5018 },
    zoom: 7,
    bounds: {
      north: 57.8,
      south: 54.4,
      east: 15.2,
      west: 8.0
    },
    region: 'dk'
  },
  'FI': {
    center: { lat: 61.9241, lng: 25.7482 },
    zoom: 5,
    bounds: {
      north: 70.1,
      south: 59.8,
      east: 31.6,
      west: 20.6
    },
    region: 'fi'
  },
  'DE': {
    center: { lat: 51.1657, lng: 10.4515 },
    zoom: 6,
    bounds: {
      north: 55.1,
      south: 47.3,
      east: 15.0,
      west: 5.9
    },
    region: 'de'
  },
  'PL': {
    center: { lat: 51.9194, lng: 19.1451 },
    zoom: 6,
    bounds: {
      north: 54.8,
      south: 49.0,
      east: 24.1,
      west: 14.1
    },
    region: 'pl'
  }
};

/**
 * Get map configuration for a specific country
 * @param {string} countryCode - ISO country code
 * @returns {Object} Map configuration object
 */
export const getMapConfigForCountry = (countryCode) => {
  const config = COUNTRY_MAP_CONFIGS[countryCode];
  if (!config) {
    console.warn(`No map config found for country: ${countryCode}, using default (Latvia)`);
    return COUNTRY_MAP_CONFIGS[FEATURES.DEFAULT_COUNTRY];
  }
  return config;
};

/**
 * Get user's current country from their profile
 * @param {Object} user - User object with profile OR direct profile object
 * @returns {string} Country code or default country
 */
export const getUserCountry = (user) => {
  // Handle multiple possible structures:
  // 1. user.profile.profile.country_code (double nested - current Supabase structure)
  // 2. user.profile.country_code (standard nested)
  // 3. user.country_code (direct)
  let countryCode;
  
  if (user?.profile?.profile?.country_code) {
    // Double-nested profile (Supabase auth user object with nested profile)
    countryCode = user.profile.profile.country_code;
  } else if (user?.profile?.country_code) {
    // User object with nested profile
    countryCode = user.profile.country_code;
  } else if (user?.country_code) {
    // Direct profile object
    countryCode = user.country_code;
  } else {
    console.warn('No country code found in user object, using default:', FEATURES.DEFAULT_COUNTRY);
    return FEATURES.DEFAULT_COUNTRY;
  }
  
  // Verify the country is still available (in case user has an old/disabled country)
  if (!isCountryAvailable(countryCode)) {
    console.warn(`User has unavailable country: ${countryCode}, using default`);
    return FEATURES.DEFAULT_COUNTRY;
  }
  
  return countryCode;
};

/**
 * Detect country from coordinates using reverse geocoding
 * @param {number} lat - Latitude
 * @param {number} lng - Longitude
 * @returns {Promise<string>} Country code or default
 */
export const detectCountryFromCoordinates = async (lat, lng) => {
  try {
    console.log(`🌍 Detecting country from coordinates: ${lat}, ${lng}`);
    
    // Use Google Maps Geocoding if available
    if (window.google && window.google.maps) {
      const geocoder = new window.google.maps.Geocoder();
      const latLng = new window.google.maps.LatLng(lat, lng);
      
      return new Promise((resolve) => {
        geocoder.geocode({ location: latLng }, (results, status) => {
          if (status === 'OK' && results[0]) {
            const countryComponent = results[0].address_components.find(
              component => component.types.includes('country')
            );
            
            if (countryComponent) {
              const countryCode = countryComponent.short_name;
              console.log(`🌍 Detected country from coordinates: ${countryCode}`);
              
              // Check if country is supported
              if (isCountryAvailable(countryCode)) {
                console.log(`✅ Using detected country: ${countryCode}`);
                resolve(countryCode);
                return;
              }
            }
          }
          
          console.log(`🏠 Could not detect country from coordinates, using default`);
          resolve(FEATURES.DEFAULT_COUNTRY);
        });
      });
    }
    
    // Fallback: use IP-based detection
    console.log('Google Maps not available, falling back to IP detection');
    return await detectUserCountry();
    
  } catch (error) {
    console.warn('Country detection from coordinates failed:', error);
    return FEATURES.DEFAULT_COUNTRY;
  }
};

/**
 * Detect user's country based on IP geolocation
 * Fallback to default country if detection fails or country is unsupported
 * @returns {Promise<string>} Country code
 */
export const detectUserCountry = async () => {
  try {
    console.log('🌍 Attempting to detect user country...');
    
    // Try multiple IP geolocation services for better reliability
    const services = [
      'https://ipapi.co/json/',
      'https://ipinfo.io/json',
      'https://api.ipgeolocation.io/ipgeo?apiKey=free'
    ];
    
    for (const service of services) {
      try {
        const response = await fetch(service, { timeout: 3000 });
        if (!response.ok) continue;
        
        const data = await response.json();
        const detectedCountry = data.country_code || data.country;
        
        console.log(`🌍 Detected country: ${detectedCountry} from ${service}`);
        
        // Check if detected country is supported
        if (detectedCountry && isCountryAvailable(detectedCountry)) {
          console.log(`✅ Using detected country: ${detectedCountry}`);
          return detectedCountry;
        }
      } catch (serviceError) {
        console.warn(`Failed to get location from ${service}:`, serviceError);
        continue;
      }
    }
    
    console.log(`🏠 No supported country detected, using default: ${FEATURES.DEFAULT_COUNTRY}`);
    return FEATURES.DEFAULT_COUNTRY;
    
  } catch (error) {
    console.warn('Country detection failed:', error);
    return FEATURES.DEFAULT_COUNTRY;
  }
};

/**
 * Format country display name with flag emoji
 * @param {string} countryCode - ISO country code
 * @returns {string} Formatted country name with flag
 */
export const formatCountryDisplay = (countryCode) => {
  const countryFlags = {
    'LV': '🇱🇻',
    'EE': '🇪🇪',
    'LT': '🇱🇹',
    'SE': '🇸🇪',
    'NO': '🇳🇴',
    'DK': '🇩🇰',
    'FI': '🇫🇮',
    'DE': '🇩🇪',
    'PL': '🇵🇱',
    'NL': '🇳🇱',
    'BE': '🇧🇪',
    'AT': '🇦🇹',
    'CZ': '🇨🇿',
    'SK': '🇸🇰'
  };
  
  const flag = countryFlags[countryCode] || '🌍';
  const name = getCountryName(countryCode);
  return `${flag} ${name}`;
};

/**
 * Get country options for form selects
 * @returns {Array} Array of {value, label} objects for form options
 */
export const getCountryOptions = () => {
  return getAvailableCountries().map(country => ({
    value: country.code,
    label: formatCountryDisplay(country.code)
  }));
};

/**
 * Validate if a country change is allowed
 * @param {string} fromCountry - Current country
 * @param {string} toCountry - Target country
 * @returns {Object} Validation result with success flag and message
 */
export const validateCountryChange = (fromCountry, toCountry) => {
  if (!toCountry) {
    return {
      success: false,
      message: 'Please select a country'
    };
  }
  
  if (!isCountryAvailable(toCountry)) {
    return {
      success: false,
      message: `${getCountryName(toCountry)} is not available yet`
    };
  }
  
  if (fromCountry === toCountry) {
    return {
      success: false,
      message: 'Country is already selected'
    };
  }
  
  return {
    success: true,
    message: `Ready to switch to ${getCountryName(toCountry)}`
  };
};

/**
 * Log country configuration for debugging
 */
export const logCountryConfig = () => {
  if (process.env.NODE_ENV === 'development') {
    console.log('🌍 Country Configuration:');
    console.log('  Multi-Country Enabled:', FEATURES.MULTI_COUNTRY_ENABLED);
    console.log('  Default Country:', FEATURES.DEFAULT_COUNTRY);
    console.log('  Available Countries:', FEATURES.AVAILABLE_COUNTRIES);
    console.log('  Configured Countries:', Object.keys(COUNTRY_MAP_CONFIGS));
  }
};

// Export all functions and constants
export {
  getCountryName,
  getAvailableCountries,
  isCountryAvailable
} from '../config/features';

export default {
  COUNTRY_MAP_CONFIGS,
  getMapConfigForCountry,
  getUserCountry,
  detectUserCountry,
  detectCountryFromCoordinates,
  formatCountryDisplay,
  getCountryOptions,
  validateCountryChange,
  logCountryConfig
};